import React, {useEffect, useState} from 'react';
import {PostListItem, PostMediaItem} from '../../../../data/models/Posts/Post';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import {Icon} from '../../../../uiToolkit/Icon/Icon';
import PostPreviewOverlay from '../../components/PostPreviewOverlay';
import PostPhoneScreen from '../../components/PostPhoneScreen';
import PostReorderColumn from '../../components/PostReorderColumn';
import {postsText} from '../../postsText';
import styles from './PostDetailsUI.module.css';

interface Props {
  post?: PostListItem;
  onAnalytics: () => void;
  items: PostMediaItem[];
  isLoading: boolean;
  onBack: () => void;
  onEditInfo: () => void;
  onLearners: () => void;
  onMoveItem: (index: number, direction: 1 | -1) => void;
  onRenameItem: (item: PostMediaItem) => void;
  onRemoveItem: (item: PostMediaItem) => void;
  busyIndexes: number[];
}

const PostDetailsUI = React.memo(
  ({
    post,
    onAnalytics,
    items,
    isLoading,
    onBack,
    onEditInfo,
    onLearners,
    onMoveItem,
    onRenameItem,
    onRemoveItem,
    busyIndexes,
  }: Props) => {
    const [previewShown, setPreviewShown] = useState(false);
    const isBusy = busyIndexes.length > 0;
    const [selected, setSelected] = useState(0);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
      setSelected(current => Math.max(Math.min(current, items.length - 1), 0));
    }, [items.length]);

    if (!post) {
      return (
        <div className={styles.loading}>
          <LoadingSpinner size={48} />
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <button className={styles.backLink} onClick={onBack}>
          ← {postsText.allPosts}
        </button>
        <div className={styles.headerRow}>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>{post.title}</h1>
          </div>
          <div className={styles.actions}>
            <button
              className={styles.textAction}
              onClick={() => setPreviewShown(true)}
              disabled={!items.length}>
              {postsText.preview}
            </button>
            <button className={styles.textAction} onClick={onEditInfo}>
              {postsText.edit}
            </button>
            <button
              className={styles.textAction}
              onClick={onAnalytics}
              disabled={!post.publishedCourseId}
              title={
                post.publishedCourseId
                  ? undefined
                  : postsText.analyticsNeedPublish
              }>
              {postsText.analytics}
            </button>
            <span className={styles.actionsDivider} />
            <button
              className={styles.shareButton}
              onClick={onLearners}
              disabled={!post.publishedCourseId}>
              {postsText.shareWithLearners}
            </button>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.preview}>
            <div className={styles.phone}>
              <PostPhoneScreen
                items={items}
                title={post.title}
                caption={post.description}
                index={selected}
                onIndexChange={setSelected}
                isLoading={isLoading}
                onProgress={setProgress}
              />
            </div>
            <div className={styles.controls}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setSelected(selected - 1)}
                disabled={selected <= 0}
                aria-label={postsText.moveLeft}>
                <span className={`icon-chevron-right ${styles.flipped}`} />
              </button>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setSelected(selected + 1)}
                disabled={selected >= items.length - 1}
                aria-label={postsText.moveRight}>
                <span className="icon-chevron-right" />
              </button>
            </div>
          </div>

          <div className={styles.rightColumn}>
            <div className={styles.listHeader}>
              <h2 className={styles.listHeading}>{postsText.mediaHeading}</h2>
              {items.length > 1 && (
                <span className={styles.listHint}>
                  {postsText.dragOrderHint}
                </span>
              )}
            </div>

            <div className={styles.itemsList}>
              {!items.length && !isLoading && (
                <p className={styles.emptyMedia}>
                  {postsText.mediaUnavailable}
                </p>
              )}
              {items.map((item, index) => (
                <div
                  className={`${styles.itemRow} ${
                    index === selected ? styles.itemRowSelected : ''
                  }`}
                  key={item.id}
                  role="presentation"
                  onClick={() => setSelected(index)}>
                  <PostReorderColumn
                    index={index}
                    total={items.length}
                    onMove={onMoveItem}
                    busy={busyIndexes.includes(index)}
                    disabled={isBusy}
                  />
                  <div className={styles.thumbnail}>
                    <img
                      className={styles.thumbnailImage}
                      src={item.isVideo ? item.poster : item.url}
                      alt=""
                    />
                    {index === selected ? (
                      <span
                        className={styles.thumbnailProgress}
                        style={{width: `${progress * 100}%`}}
                      />
                    ) : (
                      item.isVideo && (
                        <span className={styles.playBadge}>
                          <Icon
                            name="play_ic.svg"
                            className={styles.playIcon}
                          />
                        </span>
                      )
                    )}
                  </div>
                  <div className={styles.itemInfo}>
                    <p className={styles.itemTitle}>{item.title}</p>
                    <p className={styles.itemMeta}>
                      {item.isVideo
                        ? postsText.videoLabel
                        : postsText.imageLabel}
                    </p>
                    {index === selected && (
                      <span className={styles.nowPlayingChip}>
                        <span className={styles.equalizer}>
                          <span className={styles.equalizerBar} />
                          <span className={styles.equalizerBar} />
                          <span className={styles.equalizerBar} />
                        </span>
                        {postsText.nowPlaying}
                      </span>
                    )}
                  </div>
                  <div
                    className={styles.itemActions}
                    onClick={ev => ev.stopPropagation()}
                    role="presentation">
                    <button
                      className={styles.itemActionButton}
                      onClick={() => onRenameItem(item)}
                      disabled={isBusy}
                      title={postsText.renameMedia}>
                      <Icon name="edit.svg" className={styles.itemActionIcon} />
                    </button>
                    <button
                      className={styles.itemActionButton}
                      onClick={() => onRemoveItem(item)}
                      disabled={isBusy || items.length === 1}
                      title={
                        items.length === 1
                          ? postsText.lastMediaHint
                          : postsText.removeMedia
                      }>
                      <Icon
                        name="trash-icon.svg"
                        className={styles.itemActionIcon}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.ctaCard}>
              <div className={styles.ctaText}>
                <p className={styles.ctaTitle}>{postsText.shareTitle}</p>
                <p className={styles.ctaHint}>{postsText.shareHint}</p>
              </div>
              <div className={styles.ctaActions}>
                <button
                  className={styles.ctaDarkButton}
                  onClick={onLearners}
                  disabled={!post.publishedCourseId}>
                  {postsText.shareWithLearners}
                </button>
              </div>
            </div>
          </div>
        </div>

        <PostPreviewOverlay
          shown={previewShown}
          items={items}
          title={post.title}
          caption={post.description}
          onClose={() => setPreviewShown(false)}
        />
      </div>
    );
  },
);

export default PostDetailsUI;
